import { computed, inject, provide, readonly, toValue, type InjectionKey, type MaybeRefOrGetter, type Ref } from 'vue';
import {
    useAcademyTrainingsListingFetch,
    useAcademyTrainingsListingPageFetch
} from '~/composables/useAcademyTrainingsListingFetch';
import type { AcademyTrainingsListingResponse } from '~/types/academy/academy';
import type { AcademyFilter, AcademyFilterState } from '~/types/academy/filters';
import type { AcademyTrainingsListingSortSlug } from '~/types/academy/trainings-listing-sort';
import type { AcademyTrainingsListingTab } from '~/types/academy/trainings-listing-tab';
import {
    ACADEMY_TRAININGS_LISTING_SORTS,
    DEFAULT_ACADEMY_TRAININGS_LISTING_SORT_SLUG,
    DEFAULT_ACADEMY_TRAININGS_LISTING_TAB,
    TRAININGS_LISTING_URL_FILTER_CODES,
    isHiddenFilterCode,
    type FILTER_FIELDS
} from '~/utils/academy/constants';
import {
    buildAcademyTrainingsListingQuery,
    mapFilterStateToTrainingsListingQuery
} from '~/utils/academy/trainings/buildTrainingsListingQuery';
import { withResolvedCtaUrl, withTeaserColor } from '~/utils/academy/trainings/enrichTrainingsListingItem';
import { getStatusFilterKeysForTab } from '~/utils/academy/trainings/getStatusFilterKeysForTab';
import {
    validateTrainingsListingFilters,
    type AcademyTrainingsListingUrlState,
    type TrainingsListingFilterSelections
} from '~/utils/academy/trainings/trainingsListingUrlState';

export type AcademyTrainingsFixedFilters = Partial<Record<(typeof FILTER_FIELDS)[number], string>>;

export const MAX_OCCURRENCES_PER_EVENT = 8;

interface AcademyTrainingsListingOptions {
    /** Gives the root folder to the first request. The setter `setRootFolder` runs after that request. */
    readonly rootFolder: MaybeRefOrGetter<string | undefined>;
    /** Starts the state with these values. The first request, also in the server render, uses them. */
    readonly initialState?: AcademyTrainingsListingUrlState;
}

/** The user-facing state. It is one object, so that a change of more than one part gives one request. */
interface ListingSelection {
    readonly tab: AcademyTrainingsListingTab;
    readonly sort: AcademyTrainingsListingSortSlug;
    readonly filterState: AcademyFilterState;
}

/**
 * A page appended by "load more", held as the async data entry it was registered under rather than
 * as a copy of its items: a refresh writes back into `data`, and the list follows.
 */
type LoadedListingPage = {
    key: string;
    data: Ref<AcademyTrainingsListingResponse | null | undefined>;
};

const isRenderableCheckboxFilter = (filter: AcademyFilter): boolean => {
    if (filter.type === 'checkbox_filter') {
        return Array.isArray(filter.params.options) && filter.params.options.length > 0;
    }

    return false;
};

/**
 * Creates an entry for each filter that the sidebar shows. The hidden codes have no entry: the tab
 * is the only source of `status` on the listing page. An entry keeps only the selected keys that are
 * an option of its filter, in the order of the options. So the request and the URL use the same order.
 */
const createAcademyFilterState = (
    filters: readonly AcademyFilter[],
    selections: TrainingsListingFilterSelections = {}
): AcademyFilterState => {
    const validSelections = validateTrainingsListingFilters(selections, filters);

    return filters.reduce<AcademyFilterState>((state, filter) => {
        if (isRenderableCheckboxFilter(filter) && !isHiddenFilterCode(filter.code)) {
            state[filter.code] = {
                type: 'checkbox_filter',
                code: filter.code,
                selectedKeys: validSelections[filter.code] ?? []
            };
        }

        return state;
    }, {});
};

/**
 * Creates the entries before the filter options exist. `syncFilterState` validates the values
 * when the options arrive.
 */
const createUnvalidatedFilterState = (selections: TrainingsListingFilterSelections = {}): AcademyFilterState =>
    TRAININGS_LISTING_URL_FILTER_CODES.reduce<AcademyFilterState>((state, code) => {
        const selectedKeys = selections[code];

        if (selectedKeys?.length) {
            state[code] = { code, type: 'checkbox_filter', selectedKeys: [...selectedKeys] };
        }

        return state;
    }, {});

const getSelectedKeysByCode = (filterState: AcademyFilterState): Record<string, string[]> =>
    Object.fromEntries(
        Object.entries(filterState)
            .filter(([, value]) => value.selectedKeys.length > 0)
            .map(([code, value]) => [code, value.selectedKeys])
    );

function _useAcademyTrainingsListing(options?: AcademyTrainingsListingOptions) {
    const localizedPath = useLocalizedPath();
    const academyPath = useRequiredSectionPathById('academy');
    const { urlSegment } = useLanguage();

    const selection = shallowRef<ListingSelection>({
        tab: options?.initialState?.tab ?? DEFAULT_ACADEMY_TRAININGS_LISTING_TAB,
        sort: options?.initialState?.sort ?? DEFAULT_ACADEMY_TRAININGS_LISTING_SORT_SLUG,
        filterState: createUnvalidatedFilterState(options?.initialState?.filters)
    });

    const createSelectionField = <Field extends keyof ListingSelection>(field: Field) =>
        computed<ListingSelection[Field]>({
            get: () => selection.value[field],
            set: (value) => {
                selection.value = { ...selection.value, [field]: value };
            }
        });

    const tab = createSelectionField('tab');
    const sort = createSelectionField('sort');
    const filterState = createSelectionField('filterState');

    // Two steps. `shallowRef(ref)` returns the same ref, and `setRootFolder` would then write to the ref of the
    // caller.
    const rootFolderSource = shallowRef<MaybeRefOrGetter<string | undefined>>();
    rootFolderSource.value = options?.rootFolder;
    const resolvedRootFolder = computed(() => {
        const folder = rootFolderSource.value ? toValue(rootFolderSource.value) : undefined;
        return folder?.trim() || undefined;
    });

    function setRootFolder(folder: MaybeRefOrGetter<string | undefined>) {
        rootFolderSource.value = folder;
    }

    const pageSizeSource = shallowRef<MaybeRefOrGetter<number | undefined>>();
    const resolvedPageSize = computed(() => (pageSizeSource.value ? toValue(pageSizeSource.value) : undefined));

    function setPageSize(size: MaybeRefOrGetter<number | undefined>) {
        pageSizeSource.value = size;
    }

    /** Query params applied to every fetch, independent of the user-facing filter state (e.g. preset type filters). */
    const fixedFiltersSource = shallowRef<MaybeRefOrGetter<AcademyTrainingsFixedFilters | undefined>>();
    const resolvedFixedFilters = computed(() =>
        fixedFiltersSource.value ? toValue(fixedFiltersSource.value) : undefined
    );

    function setFixedFilters(filters: MaybeRefOrGetter<AcademyTrainingsFixedFilters | undefined>) {
        fixedFiltersSource.value = filters;
    }

    const listingQuery = computed(() => {
        const statusKeys = getStatusFilterKeysForTab(tab.value);
        const { key: sortKey, direction: sortDirection } = ACADEMY_TRAININGS_LISTING_SORTS[sort.value];

        return {
            max_occurrences_per_event: MAX_OCCURRENCES_PER_EVENT,
            // The tab "all" has no status keys, so the fixed `status` of a carousel stays.
            ...(statusKeys.length ? { status: statusKeys.join(',') } : {}),
            ...resolvedFixedFilters.value,
            filterState: filterState.value,
            sort_by: sortKey,
            sort_by_direction: sortDirection,
            ...(resolvedPageSize.value ? { page_size: resolvedPageSize.value } : {}),
            ...(resolvedRootFolder.value ? { root_folder: resolvedRootFolder.value } : {})
        };
    });
    const listingQuerySignature = computed(() => JSON.stringify(buildAcademyTrainingsListingQuery(listingQuery.value)));

    const { data: listingData, error, pending } = useAcademyTrainingsListingFetch(listingQuery);
    const fetchListingPage = useAcademyTrainingsListingPageFetch();

    const loadedPage = ref(1);
    // Shallow so the entries keep their `data` refs intact - a deep ref would unwrap them.
    const appendedPages = shallowRef<LoadedListingPage[]>([]);
    const loadingMore = ref(false);

    const filterOptions = computed(() => listingData.value?.filterOptions ?? []);
    const visibleFilters = computed(() => filterOptions.value.filter(isRenderableCheckboxFilter));

    const prettyError = computed(() => (error.value ? JSON.stringify(error.value, null, 2) : null));
    const showActions = computed(() => !pending.value && !prettyError.value && visibleFilters.value.length > 0);

    const hasActiveFilters = computed(
        () =>
            tab.value !== DEFAULT_ACADEMY_TRAININGS_LISTING_TAB ||
            Object.keys(mapFilterStateToTrainingsListingQuery(filterState.value)).length > 0
    );

    const urlState = computed<AcademyTrainingsListingUrlState>(() => {
        const selections = getSelectedKeysByCode(filterState.value);

        return {
            // Before the options exist, the values are not validated yet.
            filters: filterOptions.value.length
                ? validateTrainingsListingFilters(selections, filterOptions.value)
                : selections,
            tab: tab.value,
            sort: sort.value
        };
    });

    const firstPageItems = computed(() => listingData.value?.items ?? []);
    const appendedItems = computed(() => appendedPages.value.flatMap((page) => page.data.value?.items ?? []));
    const items = computed(() =>
        [...firstPageItems.value, ...appendedItems.value]
            .map(withTeaserColor)
            .map((item) => withResolvedCtaUrl(item, localizedPath, urlSegment.value, academyPath.value))
    );
    const totalCount = computed(() => listingData.value?.total_count ?? 0);
    const hasMoreItems = computed(() => items.value.length > 0 && items.value.length < totalCount.value);
    const isInitialLoading = computed(() => Boolean(pending.value) && items.value.length === 0);
    const showNoResults = computed(
        () => !pending.value && listingData.value != null && !error.value && items.value.length === 0
    );

    const filterStateReady = ref(false);

    function syncFilterState(filters: AcademyFilter[]) {
        filterState.value = createAcademyFilterState(filters, getSelectedKeysByCode(filterState.value));
    }

    // A response without filter options has no valid selection. So the state drops the sidebar selections
    // and is ready. A failed or missing response (no Docebo token) leaves the state as it is.
    watch(
        listingData,
        (response) => {
            if (!response) {
                return;
            }

            syncFilterState(response.filterOptions);
            filterStateReady.value = true;
        },
        { immediate: true }
    );

    watch(listingQuerySignature, () => {
        // These entries describe a result set that no longer exists. Left registered they would sit
        // in `payload.data` and a refresh would refetch pages that are not on screen any more.
        if (appendedPages.value.length) {
            clearNuxtData(appendedPages.value.map((page) => page.key));
            appendedPages.value = [];
        }

        loadedPage.value = 1;
    });

    function toggleCheckboxOption(filterCode: string, optionKey: string) {
        const value = filterState.value[filterCode];

        if (!value) {
            return;
        }

        const selectedKeys = new Set(value.selectedKeys);

        if (!selectedKeys.delete(optionKey)) {
            selectedKeys.add(optionKey);
        }

        const options = filterOptions.value.find((filter) => filter.code === filterCode)?.params.options ?? [];

        filterState.value = {
            ...filterState.value,
            [filterCode]: {
                ...value,
                selectedKeys: options.map((option) => option.key).filter((key) => selectedKeys.has(key))
            }
        };
    }

    function resetFilters() {
        selection.value = {
            ...selection.value,
            tab: DEFAULT_ACADEMY_TRAININGS_LISTING_TAB,
            filterState: createAcademyFilterState(filterOptions.value)
        };
    }

    function applyUrlState(state: AcademyTrainingsListingUrlState) {
        selection.value = {
            tab: state.tab,
            sort: state.sort,
            filterState: filterOptions.value.length
                ? createAcademyFilterState(filterOptions.value, state.filters)
                : createUnvalidatedFilterState(state.filters)
        };
    }

    async function loadMore() {
        if (loadingMore.value || pending.value || !hasMoreItems.value) {
            return;
        }

        loadingMore.value = true;
        const pageToLoad = loadedPage.value + 1;
        const requestSignature = listingQuerySignature.value;

        try {
            const pageFetch = fetchListingPage({
                ...listingQuery.value,
                page: pageToLoad
            });

            await pageFetch;

            // A filter change while this was in flight retargets the list, so the page it answered
            // for is gone. An empty or failed page has nothing to append either; both leave an entry
            // behind that only the refresh would find, so drop it rather than keep it registered.
            if (listingQuerySignature.value !== requestSignature || !pageFetch.data.value?.items?.length) {
                clearNuxtData(pageFetch.key);
                return;
            }

            appendedPages.value = [...appendedPages.value, { key: pageFetch.key, data: pageFetch.data }];
            loadedPage.value = pageToLoad;
        } finally {
            loadingMore.value = false;
        }
    }

    const isFilterStateReady = readonly(filterStateReady);

    return {
        setRootFolder,
        setPageSize,
        setFixedFilters,
        filterState,
        tab,
        sort,
        isFilterStateReady,
        urlState,
        applyUrlState,
        filterOptions,
        visibleFilters,
        prettyError,
        showActions,
        hasActiveFilters,
        toggleCheckboxOption,
        resetFilters,
        items,
        totalCount,
        hasMoreItems,
        isInitialLoading,
        loadingMore,
        showNoResults,
        loadMore,
        error,
        pending
    };
}

const academyTrainingsListingKey: InjectionKey<ReturnType<typeof _useAcademyTrainingsListing>> =
    Symbol('academyTrainingsListing');

/**
 * The listing state of the trainings listing blok. The first call (in the blok) creates the state
 * and gives it to all child components. The options of later calls have no effect.
 *
 * This is not `createSharedComposable`. On the server, that function gives each caller its own state,
 * and each state would send its own listing request.
 */
export function useAcademyTrainingsListing(options?: AcademyTrainingsListingOptions) {
    const providedListing = inject(academyTrainingsListingKey, undefined);

    if (providedListing) {
        return providedListing;
    }

    const listing = _useAcademyTrainingsListing(options);
    provide(academyTrainingsListingKey, listing);

    return listing;
}

/** Non-shared variant for self-contained bloks (e.g. carousels) that must not clash with the shared listing state. */
export const useScopedAcademyTrainingsListing = _useAcademyTrainingsListing;
